/**
 * useConstructionDeleteOperations Hook
 * 
 * Custom React hook for handling delete operations on construction worker salary data.
 * Provides delete functionality with loading, error, and success state management.
 * 
 * Features:
 * - Delete by month (all records for a year/month)
 * - Delete by year (all records for a year)
 * - Delete by site (現場/genba) - all records for a site
 * - Delete by employee name - all records for an employee
 * - Delete individual records
 * - Delete all data (dangerous operation)
 * - Confirmation dialogs before deletion (for individual records)
 * - Automatic data refresh after successful deletion
 * - Loading, error, and success message management
 * - Uses authenticatedFetch for API calls
 * 
 * Note: Construction workers use different fields than office employees:
 * - Site (現場/genbaName) instead of department
 * - Employee name instead of employee code
 * - No separate salary/bonus types
 * 
 * Used by:
 * - ConstructionDataDisplay component for construction worker data display
 * 
 * @module useConstructionDeleteOperations
 */

import { useState } from 'react';
import { API_BASE_URL } from '../../../../../config/apiConfig';
import { authenticatedFetch } from '../../../../../utils/authenticatedFetch';
import { CONSTRUCTION_BONUS_API_BASE_PATH } from '../../../../../utils/constructionBonusContract';
import { buildConstructionSelectionLabel } from '../utils/deleteSelectionLabel';

/**
 * useConstructionDeleteOperations Hook
 * 
 * Custom hook for handling delete operations on construction worker data.
 * Manages delete operations through authenticatedFetch API calls.
 * 
 * @param onDataChanged - Callback function to refresh data after successful deletion
 * @returns Object containing delete state and handler functions
 * 
 * @example
 * ```typescript
 * const {
 *   deleteLoading,
 *   deleteError,
 *   deleteSuccess,
 *   handleDeleteMonthData,
 *   handleDeleteYearData,
 *   handleDeleteGenbaData,
 *   handleDeleteEmployeeData,
 *   handleDeleteIndividualRecord,
 *   handleDeleteAllData,
 *   clearDeleteMessages
 * } = useConstructionDeleteOperations(() => fetchFilteredData());
 * ```
 */
export const useConstructionDeleteOperations = (
  onDataChanged: () => void,
  dataType: 'salary' | 'bonus' = 'salary'
) => {
  /**
   * State management:
   * - deleteLoading: Loading state for delete operations
   * - deleteError: Error message if delete fails (empty string if no error)
   * - deleteSuccess: Success message after delete (empty string if no success)
   */
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string>('');
  const [deleteSuccess, setDeleteSuccess] = useState<string>('');

  /**
   * Clears delete operation messages (error and success).
   * 
   * Resets both deleteError and deleteSuccess to empty strings.
   * Used to dismiss messages in the UI.
   */
  const apiBase = dataType === 'bonus'
    ? `${API_BASE_URL}${CONSTRUCTION_BONUS_API_BASE_PATH}`
    : `${API_BASE_URL}/construction-salary`;
  const kindLabel = dataType === 'bonus' ? '賞与' : '給与';

  const clearDeleteMessages = () => {
    setDeleteError('');
    setDeleteSuccess('');
  };

  /**
   * Handles deletion of all data for a specific month.
   * 
   * Validates that both year and month are provided, then deletes all records
   * for that month and year.
   * 
   * @param year - Year string to filter deletion
   * @param month - Month string to filter deletion
   * 
   * @example
   * ```typescript
   * await handleDeleteMonthData('2024', '1'); // Delete all January 2024 data
   * ```
   */
  const handleDeleteMonthData = async (year: string, month: string) => {
    if (!year || !month) return;

    setDeleteLoading(true);
    setDeleteError('');
    setDeleteSuccess('');

    try {
      const response = await authenticatedFetch(`${apiBase}/delete/month`, {
        method: 'DELETE',
        body: JSON.stringify({ year: parseInt(year), month: parseInt(month) })
      });

      const result = await response.json();

      if (result.success) {
        setDeleteSuccess(`${year}年${month}月の建設作業員${kindLabel}データを削除しました。`);
        onDataChanged();
      } else {
        setDeleteError(result.message || '削除に失敗しました。');
      }
    } catch (error: any) {
      setDeleteError('削除中にエラーが発生しました: ' + error.message);
      console.error('Delete month error:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeletePeriodData = async (year: string, period: string) => {
    if (!year || !period) return;

    setDeleteLoading(true);
    setDeleteError('');
    setDeleteSuccess('');

    try {
      const response = await authenticatedFetch(`${apiBase}/delete/period`, {
        method: 'DELETE',
        body: JSON.stringify({ year: parseInt(year), period })
      });

      const result = await response.json();

      if (result.success) {
        setDeleteSuccess(`${year}年${period}期の建設作業員賞与データを削除しました。`);
        onDataChanged();
      } else {
        setDeleteError(result.message || '削除に失敗しました。');
      }
    } catch (error: any) {
      setDeleteError('削除中にエラーが発生しました: ' + error.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteYearData = async (year: string) => {
    if (!year) return;

    setDeleteLoading(true);
    setDeleteError('');
    setDeleteSuccess('');

    try {
      const response = await authenticatedFetch(`${apiBase}/delete/year`, {
        method: 'DELETE',
        body: JSON.stringify({ year: parseInt(year) })
      });

      const result = await response.json();

      if (result.success) {
        setDeleteSuccess(`${year}年のすべての建設作業員${kindLabel}データを削除しました。`);
        onDataChanged();
      } else {
        setDeleteError(result.message || '削除に失敗しました。');
      }
    } catch (error: any) {
      setDeleteError('削除中にエラーが発生しました: ' + error.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteSelection = async (
    year: string,
    month: string,
    genbaName: string,
    employeeName: string
  ) => {
    if (!year || !month) {
      setDeleteError(dataType === 'bonus' ? '年と期を選択してください。' : '年と月を選択してください。');
      return;
    }

    const label = buildConstructionSelectionLabel(year, month, genbaName, employeeName, dataType);
    if (!window.confirm(`選択中の条件（${label}）の${kindLabel}データを削除しますか？この操作は取り消せません。`)) {
      return;
    }

    setDeleteLoading(true);
    setDeleteError('');
    setDeleteSuccess('');

    try {
      const body: Record<string, unknown> = {
        year: parseInt(year),
      };
      if (dataType === 'bonus') {
        body.period = month;
      } else {
        body.month = parseInt(month);
      }
      if (genbaName) body.genbaName = genbaName;
      if (employeeName) body.employeeName = employeeName;

      const response = await authenticatedFetch(`${apiBase}/delete/selection`, {
        method: 'DELETE',
        body: JSON.stringify(body),
      });

      const result = await response.json();

      if (result.success) {
        const count = result.deletedCount ?? 0;
        setDeleteSuccess(`選択条件（${label}）の${kindLabel}データを${count}件削除しました。`);
        onDataChanged();
      } else {
        setDeleteError(result.message || '削除に失敗しました。');
      }
    } catch (error: any) {
      setDeleteError('削除中にエラーが発生しました: ' + error.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteIndividualRecord = async (
    genbaName: string,
    employeeName: string,
    year: number,
    monthOrPeriod: number | string
  ) => {
    const period = String(monthOrPeriod);
    if (!genbaName || !employeeName || !year || !period) return;

    const unit = dataType === 'bonus' ? '期' : '月';
    const confirmed = window.confirm(
      `${genbaName}の${employeeName}さんの${year}年${period}${unit}の${kindLabel}データを削除してもよろしいですか？`
    );

    if (!confirmed) return;

    setDeleteLoading(true);
    setDeleteError('');
    setDeleteSuccess('');

    try {
      const body: Record<string, unknown> = { genbaName, employeeName, year };
      if (dataType === 'bonus') {
        body.period = period;
      } else {
        body.month = typeof monthOrPeriod === 'number' ? monthOrPeriod : parseInt(period, 10);
      }

      const response = await authenticatedFetch(`${apiBase}/delete/record`, {
        method: 'DELETE',
        body: JSON.stringify(body)
      });

      const result = await response.json();

      if (result.success) {
        setDeleteSuccess(`${employeeName}さんの${year}年${period}${unit}の${kindLabel}データを削除しました。`);
        onDataChanged();
      } else {
        setDeleteError(result.message || '削除に失敗しました。');
      }
    } catch (error: any) {
      setDeleteError('削除中にエラーが発生しました: ' + error.message);
      console.error('Delete record error:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  return {
    deleteLoading,
    deleteError,
    deleteSuccess,
    handleDeleteYearData,
    handleDeleteMonthData,
    handleDeletePeriodData,
    handleDeleteSelection,
    handleDeleteIndividualRecord,
    clearDeleteMessages
  };
};
